import loresRaw from '../data/lores.json';

export interface LoreSpell {
  id: string;
  name: string;
  number: number;
  is_signature: boolean;
  casting_value: string;
  range: string;
  type: string;
  duration: string;
  flavour: string;
  effect: string;
}

export interface Lore {
  id: string;
  name: string;
  spells?: LoreSpell[];
}

/** These are reference articles in lores.json, not actual spell lores */
const META_LORE_IDS: ReadonlySet<string> = new Set([
  'spell-categories-lores',
  'spells-and-spell-generation',
]);

/** Lores that exist only to supply bound spells (e.g. the Anvil of Doom's runes).
 *  They resolve via getLore() for bound-spell lookups but must never appear as a
 *  wizard-selectable Lore of Magic. */
const BOUND_ONLY_LORE_IDS: ReadonlySet<string> = new Set([
  'runes-of-the-anvil',
  'vortex-of-souls',
  'storm-call',
  'blessings-of-khaine',
]);

const ALL_LORES = loresRaw as unknown as Lore[];

/** Convert unit lore key (snake_case from faction data) to lores.json id (kebab-case) */
export function unitLoreToId(unitLore: string): string {
  return unitLore.replace(/_/g, '-');
}

/** Look up a lore by unit snake_case key or lores.json kebab-case id */
export function getLore(loreKey: string): Lore | undefined {
  const id = loreKey.includes('_') ? unitLoreToId(loreKey) : loreKey;
  const lore = ALL_LORES.find((l) => l.id === id);
  return lore && !META_LORE_IDS.has(lore.id) ? lore : undefined;
}

/** All playable lores (excludes meta/reference entries and bound-only rune lores) */
export function getAllPlayableLores(): Lore[] {
  return ALL_LORES.filter(
    (l) =>
      Array.isArray(l.spells) &&
      l.spells.length > 0 &&
      !META_LORE_IDS.has(l.id) &&
      !BOUND_ONLY_LORE_IDS.has(l.id),
  );
}

/** Spells for a lore, sorted signature-first then by number */
export function getLoreSpells(loreKey: string): LoreSpell[] {
  const lore = getLore(loreKey);
  if (!lore?.spells) return [];
  return [...lore.spells].sort((a, b) => a.number - b.number);
}

/**
 * The faction lore a wizard's 'Lore of …' special rule grants access to.
 *
 * Every army book prints the same mechanic (e.g. FoF p.110, Legends CD p.21):
 * a Wizard with the rule may discard one randomly generated spell and take
 * either the signature spell of their chosen Lore or a spell from the faction
 * lore instead. The rule id matches the lores.json id (snake→kebab); the
 * Renegades 2.0 and Renegade Legacy revisions have their own spell versions,
 * so prefer the faction's revision and fall back to the base lore when the
 * revision's spells have not been captured yet.
 */
export function bonusLoreForUnit(
  unit: { special_rules?: string[] },
  factionId: string,
): string | undefined {
  const rule = (unit.special_rules ?? []).find((r) => r.startsWith('lore_of_'));
  if (!rule) return undefined;
  const suffix = factionId.endsWith('-renegade-legacy') ? '_rl15'
    : factionId.endsWith('-renegades') ? '_renegade'
    : '';
  for (const key of suffix ? [rule + suffix, rule] : [rule]) {
    const lore = getLore(key);
    if (lore?.spells?.length) return key;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Spell-modifying magic items
// ---------------------------------------------------------------------------

export type SpellModItem =
  | 'lore_familiar'
  | 'spell_familiar'
  | 'tome_of_midnight'
  | 'grimoire_of_ogvold'
  | 'heartwood_pendant'
  | 'goretooth';

/** Items available to each faction. All non-Dwarf factions can access lore_familiar
 *  (it's a global item). Faction-specific extras are listed separately. */
export const FACTION_SPELL_MOD_ITEMS: Readonly<Partial<Record<string, SpellModItem[]>>> = {
  'empire-of-man':        ['lore_familiar', 'tome_of_midnight'],
  'high-elf-realms':      ['lore_familiar'],
  'kingdom-of-bretonnia': ['lore_familiar'],
  'wood-elf-realms':      ['lore_familiar', 'heartwood_pendant'],
  'warriors-of-chaos':    ['lore_familiar', 'spell_familiar', 'grimoire_of_ogvold'],
  'beastmen-brayherds':   ['lore_familiar', 'goretooth'],
  'orc-and-goblin-tribes':['lore_familiar'],
  'tomb-kings-of-khemri': ['lore_familiar'],
  'dwarfen-mountain-holds': [],
};

// ---------------------------------------------------------------------------
// Bound spell items
// ---------------------------------------------------------------------------

export interface BoundSpellItem {
  itemId: string;
  itemName: string;
  /** lores.json kebab-case lore id */
  loreId: string;
  spellId: string;
  spellName: string;
  castingValue: string;
  powerLevel: number;
  /** Spell category (Enchantment, Conveyance, Magic Missile, ...) - drives phase filtering */
  spellType?: string;
  /** Effect summary from lores.json */
  effect?: string;
  /** If set, only shown for this faction; undefined = available to all factions */
  factionId?: string;
  /** Source unit id when the bound spell comes from a unit (e.g. the Anvil of Doom's
   *  runes) rather than a magic item. Used to auto-detect from the army list. */
  unitId?: string;
}

function makeBound(
  itemId: string,
  itemName: string,
  loreId: string,
  spellId: string,
  powerLevel: number,
  factionId?: string,
): BoundSpellItem {
  const spell = getLore(loreId)?.spells?.find((s) => s.id === spellId);
  return {
    itemId,
    itemName,
    loreId,
    spellId,
    spellName: spell?.name ?? spellId,
    castingValue: spell?.casting_value ?? '?',
    powerLevel,
    spellType: spell?.type,
    effect: spell?.effect,
    factionId,
  };
}

/** Bound spell sourced from a unit (rather than a magic item), e.g. Anvil of Doom runes. */
function makeUnitBound(
  unitId: string,
  sourceName: string,
  loreId: string,
  spellId: string,
  powerLevel: number,
  factionId: string,
): BoundSpellItem {
  const spell = getLore(loreId)?.spells?.find((s) => s.id === spellId);
  return {
    itemId: `${unitId}__${spellId}`,
    itemName: sourceName,
    loreId,
    spellId,
    spellName: spell?.name ?? spellId,
    castingValue: spell?.casting_value ?? '?',
    powerLevel,
    spellType: spell?.type,
    effect: spell?.effect,
    factionId,
    unitId,
  };
}

export const BOUND_SPELL_ITEMS: ReadonlyArray<BoundSpellItem> = [
  makeBound('ruby-ring-of-ruin',          'Ruby Ring of Ruin',           'battle-magic',       'fireball',       1),
  makeBound('ring_of_fury',               'Ring of Fury',                'battle-magic',       'hammerhand',     2, 'high-elf-realms'),
  makeBound('ring_of_taal',               'Ring of Taal',                'battle-magic',       'oaken_shield',   3, 'empire-of-man'),
  makeBound('twice_blessed_armour',       'Twice-Blessed Armour',        'battle-magic',       'hammerhand',     2, 'empire-of-man'),
  makeBound('standard-of-mornings-chill', "Standard of Morning's Chill", 'lore-of-the-wilds',  'swirling_mists', 2, 'wood-elf-realms'),
  makeBound('moonstone-of-the-hidden-ways','Moonstone of the Hidden Ways','lore-of-athel-loren','forest_walker',  3, 'wood-elf-realms'),
  makeBound('rod_of_the_damned',          'Rod of the Damned',           'daemonology',        'the_summoning',  2, 'warriors-of-chaos'),
  // Anvil of Doom runes - unit-innate bound spells cast at Power Level 2 (FoF FAQ v1.5.3)
  makeUnitBound('anvil_of_doom', 'Anvil of Doom', 'runes-of-the-anvil', 'rune_of_oath_and_steel',    2, 'dwarfen-mountain-holds'),
  makeUnitBound('anvil_of_doom', 'Anvil of Doom', 'runes-of-the-anvil', 'rune_of_hearth_and_home',   2, 'dwarfen-mountain-holds'),
  makeUnitBound('anvil_of_doom', 'Anvil of Doom', 'runes-of-the-anvil', 'rune_of_haste_and_urgency', 2, 'dwarfen-mountain-holds'),
  makeUnitBound('anvil_of_doom', 'Anvil of Doom', 'runes-of-the-anvil', 'rune_of_wrath_and_ruin',    2, 'dwarfen-mountain-holds'),
  // Other unit-innate bound spells (fixed spells; player-chosen ones are handled via the caster flow)
  makeUnitBound('casket_of_souls',       'Casket of Souls (Vortex of Souls)', 'vortex-of-souls',     'light_of_death',      2, 'tomb-kings-of-khemri'),
  makeUnitBound('casket_of_souls',       'Casket of Souls (Vortex of Souls)', 'vortex-of-souls',     'light_of_protection', 2, 'tomb-kings-of-khemri'),
  makeUnitBound('dragon_ogre_shaggoth',  'Dragon Ogre Shaggoth (Storm Call)', 'storm-call',          'storm_call',          1, 'beastmen-brayherds'),
  makeUnitBound('cauldron_of_blood',     'Cauldron of Blood',                 'blessings-of-khaine', 'blessings_of_khaine', 2, 'dark-elves'),
  makeUnitBound('kralmaw_the_prophet_of_ruin', 'Kralmaw (Leering Spirit)',    'lore-of-beasts',      'devolve',             3, 'beastmen-brayherds'),
];

// ---------------------------------------------------------------------------
// Choice-caster bound spells
// ---------------------------------------------------------------------------
// Units that cast a player-CHOSEN spell (from an allowed lore) as a bound spell,
// at a power level that depends on the unit's composition. Unlike a fixed bound
// spell, the spell must be selected during game setup.

export interface ChoiceCasterDef {
  unitId: string;
  label: string;
  factionId: string;
  /** snake_case lore keys the unit may choose its spell from */
  loreKeys: string[];
  /** Discrete power levels the bound spell may be cast at */
  powerLevels: number[];
  defaultPowerLevel: number;
  /** Player-facing note explaining how the power level is determined */
  note: string;
}

export const CHOICE_CASTERS: ReadonlyArray<ChoiceCasterDef> = [
  {
    unitId: 'sisters-of-the-thorn',
    label: 'Sisters of the Thorn (Deepwood Coven)',
    factionId: 'wood-elf-realms',
    loreKeys: ['battle_magic', 'elementalism'],
    powerLevels: [0, 1, 2],
    defaultPowerLevel: 0,
    note: 'Knows a randomly generated spell (or the lore signature) from Battle Magic or Elementalism. Power Level 2 with a Handmaiden of the Thorn and a standard bearer; 1 with a Handmaiden only; otherwise 0.',
  },
  {
    unitId: 'doomfire_warlocks',
    label: 'Doomfire Warlocks (Cursed Coven)',
    factionId: 'dark-elves',
    loreKeys: ['dark_magic', 'daemonology'],
    powerLevels: [0, 1, 2],
    defaultPowerLevel: 0,
    note: 'Knows one spell from Dark Magic or Daemonology. Power Level 2 with a Master and Unit Strength 10+; 1 with a Master and US 9 or less; otherwise 0.',
  },
  {
    unitId: 'cathayan_sentinel_gc',
    label: 'Jade Cathayan Sentinel',
    factionId: 'grand-cathay',
    loreKeys: ['battle_magic', 'elementalism'],
    powerLevels: [3],
    defaultPowerLevel: 3,
    note: 'Jade Sentinel only. Casts its chosen spell from Battle Magic or Elementalism as a Bound spell at Power Level 3.',
  },
];

export function choiceCasterDef(unitId: string): ChoiceCasterDef | undefined {
  return CHOICE_CASTERS.find((c) => c.unitId === unitId);
}
